// SendMessageModal — JJ 2026-05-19
// Modal d'envoi SMS+Email depuis fiche dossier (pattern shadcn Dialog).
//
// Sources templates :
//  - SMS  → GET /api/communication/templates?channel=sms&status=active (collection `comm_templates`)
//  - Email → GET /api/governance/email-config/templates?tenant_id=... (collection `email_templates`)
//
// Endpoint d'envoi unifié :
//  - POST /api/communication/send {channel, template_id, recipient, variables, triggered_by}
//
// ALIGNEMENT #431 — cet écran lit désormais la MÊME source que l'envoi de devis
// OMV, qui fonctionne en production (`SendDevisOmvModal:55`). Il interrogeait
// `/api/design/templates?type=email_template`, c'est-à-dire la collection
// `design_templates`, où Design Studio n'écrit JAMAIS : d'où « 0 disponible »
// alors que trois templates existaient. L'en-tête ci-dessus décrivait déjà la
// bonne route ; c'est le code qui appelait l'autre.
//
// Le repli backend (`communication/service.py:get_template`) résout les `key`
// d'`email_templates` scopées tenant — il ne le faisait pas non plus avant
// #431, et la liste peuplée aurait donné un envoi en 404.
import { useState, useEffect, useMemo } from "react";
import { Send, Loader2, Mail, MessageSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { API, getAuthHeaders } from "./helpers";

// Normalise un `email_templates` (Design Studio → Emails Métier) vers le shape
// commun. Shape réel, relu en base : {_id, key, tenant_id, category, name,
//   description, subject, from_name, from_email, reply_to, variables,
//   html_body, text_body, active, owner, version, ...}
//
// ⚠️ L'identifiant transmis à l'envoi est la `key`, PAS l'ObjectId : c'est par
// elle que `get_template` résout le template, et c'est ce que fait déjà
// l'envoi de devis OMV.
// ⚠️ Il n'y a NI champ `type`, NI champ `audience`, NI champ `contenu` dans
// cette collection — l'ancienne normalisation les lisait tous les trois et
// rendait donc un objet vide. Ne pas les réintroduire.
function normalizeEmailTemplate(t) {
  return {
    id: t.key || "",
    name: t.name || "",
    description: t.description || "",
    category: (t.category || "").toLowerCase(),
    channel: "email",
    content: t.html_body || t.text_body || "",
    subject: t.subject || "",
    variables: (t.variables || []).map((v) =>
      typeof v === "string"
        ? { name: v, required: false, description: "", default_value: null }
        : {
            name: v.key || v.name,
            required: v.required || false,
            description: v.description || "",
            default_value: v.default ?? v.default_value ?? null,
          }
    ),
  };
}

// Détecte un destinataire fictif (preprod anonymisé) — bloque l'envoi
function isFakeRecipient(value) {
  if (!value) return false;
  if (typeof value !== "string") return false;
  return value.endsWith("@preprod.local") || value.startsWith("+33900");
}

// ⚠️ `audienceFilter` RETIRÉ (#431). Il filtrait sur un champ `audience` qui
// n'existe dans AUCUNE collection ni dans aucun POST : le filtre ne pouvait
// que tout laisser passer ou tout écarter, jamais discriminer. Le filtrage
// réel est `owner=design_studio` + `active`, appliqué côté serveur — sans
// `category`, sinon les deux templates TEF existants (`onboarding` et
// `notification`) resteraient invisibles.
export default function SendMessageModal({
  open, onClose, dossier, tenantId, currentUser,
}) {
  const [channel, setChannel] = useState("sms");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [recipient, setRecipient] = useState("");
  const [variables, setVariables] = useState({});
  const [sending, setSending] = useState(false);

  const client = dossier?.client || {};
  const defaultPhone = client.telephone || dossier?.telephone || "";
  const defaultEmail = client.email || dossier?.email || "";

  // Load templates pour le canal courant (2 sources distinctes)
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // SMS  → comm_templates (collection unifiée, V1 — migration Design Studio en V1.1)
    // Email → email_templates, filtrés `owner=design_studio` : ce tag écarte
    //   déjà les templates système (Gamora, AG-04, invitation magic link) sans
    //   qu'on ait à filtrer sur `category`. MÊME route que SendDevisOmvModal:55.
    const url =
      channel === "sms"
        ? `${API}/api/communication/templates?channel=sms&status=active`
        : `${API}/api/governance/email-config/templates?owner=design_studio`;

    fetch(url, { headers: getAuthHeaders(tenantId), credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        let list = [];
        if (channel === "sms") {
          list = data?.templates || [];
        } else {
          // email-config/templates renvoie {templates: [...]} — même forme que
          // celle consommée par SendDevisOmvModal.
          const raw = Array.isArray(data) ? data : (data?.templates || []);
          list = raw
            .filter((t) => t.active !== false)
            .map(normalizeEmailTemplate)
            .filter((t) => t.id);   // sans `key`, l'envoi ne pourrait pas résoudre
        }
        setTemplates(list);
        setSelectedTemplateId(list[0]?.id || "");
      })
      .catch((e) => toast.error(`Templates : ${e.message || e}`))
      .finally(() => setLoading(false));
  }, [open, channel, tenantId]);

  useEffect(() => {
    setRecipient(channel === "sms" ? defaultPhone : defaultEmail);
  }, [channel, defaultPhone, defaultEmail]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId),
    [templates, selectedTemplateId]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    const vars = {};
    for (const v of selectedTemplate.variables || []) {
      const key = v.name || v;
      const map = {
        prenom: client.prenom || "",
        nom: client.nom || "",
        reference: dossier?.reference || "",
        salutation: client.prenom ? `${client.prenom} ${client.nom || ""}`.trim() : (client.nom || ""),
        client_nom: client.nom || "",
        date: "",
        heure: "",
      };
      vars[key] = map[key] ?? v.default_value ?? "";
    }
    setVariables(vars);
  }, [selectedTemplate, client.prenom, client.nom, dossier?.reference]);

  const fakeRecipient = isFakeRecipient(recipient);

  const handleSend = async () => {
    if (!selectedTemplateId) {
      toast.error("Sélectionnez un template");
      return;
    }
    if (!recipient) {
      toast.error("Destinataire manquant");
      return;
    }
    if (fakeRecipient) {
      toast.error(`${channel === "sms" ? "Téléphone" : "Email"} fictif (preprod anonymisé) — envoi bloqué`);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/communication/send`, {
        method: "POST",
        headers: { ...getAuthHeaders(tenantId), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          channel,
          template_id: selectedTemplateId,
          recipient,
          variables,
          triggered_by: `human:${currentUser?.email || "support"}`,
          context: `dossier:${dossier?.id || dossier?._id || ""}`,
        }),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        toast.success(`${channel === "sms" ? "SMS" : "Email"} envoyé à ${recipient}`);
        onClose();
      } else {
        toast.error(`Échec : ${data?.error || `HTTP ${res.status}`}`);
      }
    } catch (e) {
      toast.error(`Erreur : ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg"
        style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}
        data-testid="send-message-modal"
      >
        <DialogHeader>
          <DialogTitle style={{ color: "var(--oh-text)" }} className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Envoyer un message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Tabs canal */}
          <div className="flex gap-2">
            {[
              { id: "sms", label: "SMS", Icon: MessageSquare },
              { id: "email", label: "Email", Icon: Mail },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`channel-${id}-tab`}
                onClick={() => setChannel(id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors"
                style={{
                  background: channel === id ? "rgba(59,130,246,0.15)" : "transparent",
                  color: channel === id ? "#60a5fa" : "var(--oh-text-muted)",
                  border: `1px solid ${channel === id ? "rgba(59,130,246,0.3)" : "var(--oh-border)"}`,
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Template select */}
          <div>
            <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
              Template ({templates.length} disponible{templates.length > 1 ? "s" : ""})
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
              </div>
            ) : templates.length === 0 ? (
              <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Aucun template {channel === "sms" ? "SMS" : "Email"} actif.{" "}
                {channel === "sms"
                  ? "Créez-en un dans Hub Communication."
                  : "Créez-en un dans Marketing → Design Studio → Emails Métier. Il sera rattaché à votre tenant automatiquement."}
              </div>
            ) : (
              <select
                data-testid="template-select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-2 py-1.5 text-xs rounded"
                style={{
                  background: "var(--oh-bg-elevated)",
                  color: "var(--oh-text)",
                  border: "1px solid var(--oh-border)",
                }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.description ? `— ${t.description}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Recipient */}
          <div>
            <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
              Destinataire ({channel === "sms" ? "Téléphone" : "Email"})
            </label>
            <input
              data-testid="recipient-input"
              type={channel === "email" ? "email" : "tel"}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={channel === "sms" ? "+33612345678" : "client@example.com"}
              className="w-full px-2 py-1.5 text-xs rounded"
              style={{
                background: "var(--oh-bg-elevated)",
                color: "var(--oh-text)",
                border: `1px solid ${fakeRecipient ? "rgba(248,113,113,0.5)" : "var(--oh-border)"}`,
              }}
            />
            {fakeRecipient && (
              <div
                data-testid="fake-recipient-warning"
                className="mt-1 text-xs px-2 py-1 rounded"
                style={{ background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)" }}
              >
                {channel === "sms" ? "Téléphone fictif" : "Email fictif"} (preprod anonymisé) — envoi bloqué
              </div>
            )}
          </div>

          {/* Variables */}
          {selectedTemplate?.variables?.length > 0 && (
            <div>
              <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
                Variables
              </label>
              <div className="space-y-2">
                {selectedTemplate.variables.map((v) => {
                  const key = v.name || v;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span
                        className="text-xs w-24 truncate"
                        style={{ color: "var(--oh-text-muted)" }}
                        title={v.description || key}
                      >
                        {key}
                        {v.required ? " *" : ""}
                      </span>
                      <input
                        data-testid={`var-${key}`}
                        value={variables[key] || ""}
                        onChange={(e) => setVariables({ ...variables, [key]: e.target.value })}
                        className="flex-1 px-2 py-1 text-xs rounded"
                        style={{
                          background: "var(--oh-bg-elevated)",
                          color: "var(--oh-text)",
                          border: "1px solid var(--oh-border)",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aperçu */}
          {selectedTemplate?.content && (
            <div
              className="p-2 rounded text-xs max-h-32 overflow-y-auto"
              style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
            >
              <div className="font-medium mb-1" style={{ color: "var(--oh-text)" }}>
                Aperçu {channel === "email" && selectedTemplate.subject ? `· ${selectedTemplate.subject}` : ""}
              </div>
              <pre className="whitespace-pre-wrap font-sans">
                {channel === "email"
                  ? selectedTemplate.content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300) + (selectedTemplate.content.length > 300 ? "…" : "")
                  : selectedTemplate.content}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Annuler
          </Button>
          <Button
            data-testid="send-btn"
            onClick={handleSend}
            disabled={sending || !selectedTemplateId || !recipient || fakeRecipient}
            className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1" />
            )}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
